/**
 * Environment schema (FE-01 §7.2, FR-FE01-29). Shared by the runtime (`env.ts`) and the build
 * (`vite.config.ts`): a staging or production build fails when a required variable is missing.
 * All values are public; secrets are never `VITE_*` variables.
 */

export type AppEnv = "local" | "staging" | "production";

export interface Env {
  env: AppEnv;
  apiBaseUrl: string;
  publicWebUrl: string;
  googleMapsBrowserKey: string;
  googleMapsMapId: string;
  sentryDsn: string;
  appVersion: string;
}

type Source = Record<string, string | undefined>;

interface VariableSpec {
  name: string;
  /** Required in staging/production builds. Local builds and dev use defaults. */
  requiredRemote: boolean;
  check?: (value: string) => string | null;
}

const isUrlOrSlash = (value: string) => {
  if (value === "/") return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:"
      ? null
      : "must be an http(s) URL or /";
  } catch {
    return "must be an http(s) URL or /";
  }
};

const isUrl = (value: string) => (value === "/" ? "must be an absolute URL" : isUrlOrSlash(value));

export const ENV_VARIABLES: readonly VariableSpec[] = [
  {
    name: "VITE_ENV",
    requiredRemote: true,
    check: (v) =>
      ["local", "staging", "production"].includes(v)
        ? null
        : "must be local, staging or production",
  },
  { name: "VITE_API_BASE_URL", requiredRemote: true, check: isUrlOrSlash },
  { name: "VITE_PUBLIC_WEB_URL", requiredRemote: true, check: isUrl },
  { name: "VITE_GOOGLE_MAPS_BROWSER_KEY", requiredRemote: true },
  { name: "VITE_GOOGLE_MAPS_MAP_ID", requiredRemote: true },
  { name: "VITE_SENTRY_DSN", requiredRemote: false, check: (v) => (v ? isUrl(v) : null) },
  { name: "VITE_APP_VERSION", requiredRemote: true },
];

/** Problems with `source` ("VITE_X: ..."); `remote` applies the staging/production requirements. */
export function envProblems(source: Source, { remote }: { remote: boolean }): string[] {
  const problems: string[] = [];
  for (const spec of ENV_VARIABLES) {
    const value = source[spec.name]?.trim() ?? "";
    if (!value) {
      if (remote && spec.requiredRemote) problems.push(`${spec.name}: required`);
      continue;
    }
    const error = spec.check?.(value);
    if (error) problems.push(`${spec.name}: ${error}`);
  }
  return problems;
}

/** True for builds that will be deployed (staging, production). */
export function isRemoteEnv(source: Source): boolean {
  return source.VITE_ENV === "staging" || source.VITE_ENV === "production";
}

/** Parse the environment; throws with every problem listed. `origin` resolves `VITE_API_BASE_URL=/`. */
export function parseEnv(source: Source, origin: string): Env {
  const problems = envProblems(source, { remote: isRemoteEnv(source) });
  if (problems.length) throw new Error(`Invalid environment: ${problems.join("; ")}`);
  const apiBaseUrl = source.VITE_API_BASE_URL?.trim() || "/";
  return {
    env: (source.VITE_ENV?.trim() || "local") as AppEnv,
    apiBaseUrl: apiBaseUrl === "/" ? origin : apiBaseUrl,
    publicWebUrl: source.VITE_PUBLIC_WEB_URL?.trim() || origin,
    googleMapsBrowserKey: source.VITE_GOOGLE_MAPS_BROWSER_KEY ?? "",
    googleMapsMapId: source.VITE_GOOGLE_MAPS_MAP_ID ?? "",
    sentryDsn: source.VITE_SENTRY_DSN ?? "",
    appVersion: source.VITE_APP_VERSION?.trim() || "dev",
  };
}
